// Numerals for both languages (round2-i18n.md §6 "Bangla digits"). Plain string
// work — no Intl dependency, so it behaves identically in Workers, browsers and
// Node regardless of which ICU data the runtime ships.
//
// GROUPING DECISION: South Asian grouping (12,34,567) is the default in BOTH
// languages. Evidence: the website's `num()` and the admin `moneyStr()` group
// with `toLocaleString('en-IN')` in en and bn alike; the customer/merchant designs
// use en-US grouping but only below one lakh (≥1 lakh is compacted to "L"/"লাখ"),
// where both groupings are identical. South Asian grouping also agrees with the
// lakh/crore compaction in format-money.ts. `grouping: 'western'` is available for
// any screen that needs 1,234,567.
import type { Lang } from './translate';
import { AsciiCode, isAsciiCode } from './ascii-codes';

const BN_DIGITS = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'] as const;

// U+2212, the typographic minus the designs use ("−৳1,200", "−12%").
export const MINUS = '−';

export type Grouping = 'south-asian' | 'western';

export interface NumberOptions {
  grouping?: Grouping; // default 'south-asian'
  maxFractionDigits?: number; // default 2
  minFractionDigits?: number; // default 0 — trailing zeros beyond this are trimmed
}

// Unconditional ASCII → Bangla digit swap. Prefer `localiseDigits`, which honours
// the language and the ASCII-code guard.
export function toBanglaDigits(input: string | number): string {
  return String(input).replace(/[0-9]/g, (d) => BN_DIGITS[Number(d)] ?? d);
}

// Digits in the active language. Never touches identifiers (phones, TRX ids,
// booking codes, OTPs) — see ascii-codes.ts. Does not group; use formatNumber.
export function localiseDigits(lang: Lang, value: string | number | AsciiCode): string {
  if (value instanceof AsciiCode) return value.value;
  const s = String(value);
  if (lang !== 'bn' || isAsciiCode(s)) return s;
  return toBanglaDigits(s);
}

// Groups a run of ASCII integer digits: western 1,234,567 / south-asian 12,34,567.
export function groupDigits(intDigits: string, grouping: Grouping = 'south-asian'): string {
  if (intDigits.length <= 3) return intDigits;
  if (grouping === 'western') return intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const head = intDigits.slice(0, -3);
  const tail = intDigits.slice(-3);
  return head.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + tail;
}

// Rounds to `max` fraction digits, then trims trailing zeros down to `min`.
// ("1.50" → "1.5", "2.00" → "2" with min 0; "2.0" kept with min 1.)
export function toFixedTrimmed(abs: number, max: number, min = 0): string {
  const factor = 10 ** max;
  let s = (Math.round(abs * factor) / factor).toFixed(max);
  if (max > min && s.includes('.')) {
    const [int, frac = ''] = s.split('.');
    let f = frac;
    while (f.length > min && f.endsWith('0')) f = f.slice(0, -1);
    s = f.length ? `${int}.${f}` : (int ?? '0');
  }
  return s;
}

// Grouped number in ASCII digits (language-neutral). Used by formatNumber and
// by format-money, which localises after adding its own parts.
export function formatNumberAscii(n: number, opts: NumberOptions = {}): string {
  if (!Number.isFinite(n)) return String(n);
  const { grouping = 'south-asian', maxFractionDigits = 2, minFractionDigits = 0 } = opts;
  const fixed = toFixedTrimmed(Math.abs(n), maxFractionDigits, minFractionDigits);
  const [int = '0', frac] = fixed.split('.');
  const body = groupDigits(int, grouping) + (frac ? `.${frac}` : '');
  // "-0" after rounding is just "0".
  const negative = n < 0 && /[1-9]/.test(fixed);
  return (negative ? MINUS : '') + body;
}

// en "12,34,567" / bn "১২,৩৪,৫৬৭"; decimals keep "." in both languages.
export function formatNumber(lang: Lang, n: number, opts: NumberOptions = {}): string {
  const s = formatNumberAscii(n, opts);
  return lang === 'bn' ? toBanglaDigits(s) : s;
}
